import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  X,
  ShieldCheck,
  Eye,
  Brain,
  MapPin,
  TrendingDown,
  Coins,
  Sparkles,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import Layout from '../components/layout/layout';
import Section from '../components/ui/section';
import SectionBackground from '../components/sections/section-background';
import HeroTemplateEnts from '../components/sections/HeroTemplateEnts';
import LinkedInRecommendations from '../components/sections/LinkedInRecommendations';
import {
  WebPageSchema,
  BreadcrumbSchema,
  FAQPageSchema,
} from '../lib/utils/schema-utils';

// — Typography aligned —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Tableau comparatif principal —
interface CompareRow {
  axis: string;
  icon: typeof Eye;
  classic: string;
  io: string;
  ioHighlight: string;
}

const COMPARE_ROWS: CompareRow[] = [
  {
    axis: "Expérience candidat avant signature",
    icon: Eye,
    classic:
      "Deux entretiens + fiche de poste. Le candidat signe sans avoir vu la réalité de l'équipe, du quotidien, des chiffres.",
    io: "Plateforme Parcours Découverte propriétaire — vidéos équipe, grille de rémunération, journée type, locaux. Le candidat décide en connaissance de cause.",
    ioHighlight: 'Plateforme Parcours Découverte',
  },
  {
    axis: 'Méthode de cadrage du besoin',
    icon: Sparkles,
    classic:
      "Brief express : 30 minutes au téléphone + une fiche de poste générique. Le cabinet livre 'des CV qui collent'.",
    io: "Diagnostic d'enjeu business (1h30 minimum) : pourquoi ce poste, pour quel résultat, dans quel contexte managérial. On reformule jusqu'à ce que le brief soit chirurgical.",
    ioHighlight: 'Diagnostic business',
  },
  {
    axis: 'Garantie contractuelle',
    icon: ShieldCheck,
    classic:
      "Garantie 3 mois floue, conditions opaques. Si ça lâche, vous attendez 6 mois pour le replacement (ou rien du tout).",
    io: "Garantie 6 mois opposable : replacement gratuit OU 30 % du fee restitué si vous refusez le replacement. Diagnostic post-mortem honnête fourni.",
    ioHighlight: 'Garantie 6 mois opposable',
  },
  {
    axis: 'Transparence tarifaire',
    icon: Coins,
    classic:
      "Tarifs cachés, fourchettes vagues, négociation au cas par cas. Vous découvrez le coût réel au moment de signer.",
    io: "Tarifs publics sur le site. Deux modèles : retainer (1500€ + succès) ou succès pur. Tarif chiffré au premier appel — pas après 3 entretiens.",
    ioHighlight: 'Tarifs publics',
  },
  {
    axis: 'IA appliquée au recrutement',
    icon: Brain,
    classic:
      "Aucune. Ou un slide marketing 'on utilise l'IA' qui se traduit par un ATS classique.",
    io: "Outils maison opérationnels : Mapping RH (compatibilité candidat↔équipe), scoring sémantique CV, génératif marque employeur. Précurseurs depuis 2022.",
    ioHighlight: 'Outils maison IA',
  },
  {
    axis: 'Ancrage local',
    icon: MapPin,
    classic:
      "Cabinet parisien qui 'couvre la PACA' par défaut. Aucune connaissance fine des bassins, des codes, des salaires pratiqués localement.",
    io: "Cabinet basé à Cassis (13260), couverture Marseille, Aix, Toulon, Nice, et Poitiers (via partenariat Thomaz RH). Connaissance fine des bassins.",
    ioHighlight: 'Cabinet local PACA',
  },
  {
    axis: 'Taux de no-show post-signature',
    icon: TrendingDown,
    classic:
      "1 candidat sur 5 ne se présente pas le jour J (source : Talent Board). Vous payez le fee, et vous recommencez à zéro.",
    io: "Réduction observée à -50 % grâce au Parcours Découverte : le candidat s'est projeté avant de signer, il tient.",
    ioHighlight: '-50 % de no-show',
  },
  {
    axis: 'Suivi post-embauche',
    icon: ShieldCheck,
    classic:
      "Vous payez la facture, le cabinet disparaît. Pas de suivi 90 jours, pas de mesure de la satisfaction réelle.",
    io: "Suivi 30/60/90 jours chiffré. Diagnostic onboarding partagé. Replacement gratuit si rupture < 6 mois, sans condition.",
    ioHighlight: 'Suivi 90 jours chiffré',
  },
];

// — Section 4 — FAQ comparative —
const FAQ_ITEMS = [
  {
    q: "Vous êtes un cabinet de recrutement ou une plateforme SaaS ?",
    a: "Un cabinet de recrutement à Marseille. Notre différenciateur : on a développé en interne une Plateforme Parcours Découverte propriétaire (asset tech) qui rend l'expérience candidat unique. C'est un cabinet AUGMENTÉ — le service humain comme tout cabinet, plus une couche logicielle que les autres n'ont pas.",
  },
  {
    q: "Pourquoi vos tarifs sont publics alors que personne ne le fait ?",
    a: "Parce que cacher les tarifs est un héritage de l'époque où la rareté de l'information créait le pricing power. Aujourd'hui, le client compare. On préfère le faire gagner du temps : prix transparent dès le premier appel, négociation honnête, pas de marges cachées.",
  },
  {
    q: "Vous travaillez aussi en dehors de PACA ?",
    a: "Notre cœur d'activité est PACA (Marseille, Aix, Toulon, Nice). Nous opérons aussi à Poitiers via notre partenariat avec Thomaz RH (cabinet de conseil SIRH). Pour le reste de la France, nous intervenons sur des missions ciblées en chasse de cadres dirigeants ou postes critiques.",
  },
  {
    q: "Quelle différence avec une plateforme comme Welcome to the Jungle ou Teamtailor ?",
    a: "Welcome to the Jungle / Teamtailor / Recruitee sont des SaaS — vous achetez un outil et vous gérez le recrutement vous-même. Nous, on fait le recrutement à votre place, et on vous donne accès à notre plateforme pour scénariser l'expérience candidat. Service vs logiciel.",
  },
  {
    q: "Votre garantie 6 mois, c'est vraiment opposable ?",
    a: "Oui — contractuel, par écrit, signé avec la mission. Trois cas possibles si rupture < 6 mois : (1) replacement gratuit, (2) 30 % du fee restitué si vous refusez le replacement, (3) diagnostic post-mortem honnête fourni dans tous les cas. C'est le seul moyen de prouver qu'on a confiance dans notre méthode.",
  },
  {
    q: "Combien de temps prend un recrutement avec vous ?",
    a: "Cadrage 1 semaine, sourcing + premiers entretiens 3-5 semaines, finalisation 1-2 semaines. Délai moyen de signature à 6-8 semaines pour un poste de cadre, contre 3-4 mois pour le marché. Le Parcours Découverte raccourcit aussi la phase de décision candidat (de 2-3 semaines à quelques jours).",
  },
];

export default function PourquoiRecruteursIoPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Pourquoi recruteurs.io vs cabinet de recrutement classique | Comparatif"
      description="Comparatif détaillé : recruteurs.io (Marseille, PACA) vs cabinet de recrutement classique. Plateforme Parcours Découverte propriétaire, garantie 6 mois opposable, tarifs publics, IA appliquée. Pourquoi choisir le cabinet augmenté."
      keywords="cabinet de recrutement comparatif, alternative cabinet recrutement, recruteurs.io vs, parcours candidat, garantie recrutement, cabinet recrutement marseille"
    >
      <WebPageSchema
        page={{
          title: 'Pourquoi recruteurs.io vs cabinet de recrutement classique',
          description:
            'Comparatif détaillé : recruteurs.io vs cabinet classique. Plateforme Parcours Découverte, garantie 6 mois, tarifs publics, IA appliquée.',
          url: 'https://recruteurs.io/pourquoi-recruteurs-io',
          lastReviewed: '2026-05-15',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          {
            name: 'Pourquoi recruteurs.io',
            url: 'https://recruteurs.io/pourquoi-recruteurs-io',
          },
        ]}
      />

      <FAQPageSchema
        questions={FAQ_ITEMS.map((it) => ({
          question: it.q,
          answer: it.a,
        }))}
      />

      <HeroTemplateEnts
        page="pourquoi"
        title="Pourquoi nous, plutôt qu'un cabinet de recrutement classique."
        eyebrow="Comparatif · Cabinet augmenté · Marseille · Aix · PACA"
        description="Huit axes concrets sur lesquels nous nous distinguons. Pas de marketing — du contractuel : Plateforme Parcours Découverte, garantie 6 mois opposable, tarifs publics, IA appliquée, cabinet local. À comparer, ligne par ligne."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp"
        breadcrumbs={[
          { name: 'Accueil', href: '/' },
          { name: 'Pourquoi recruteurs.io', href: '/pourquoi-recruteurs-io' },
        ]}
        cta1={{
          label: 'Réserver un échange (15 min)',
          onClick: () => navigate('/reservation'),
        }}
        cta2={{
          label: 'Voir notre Plateforme Parcours Découverte',
          onClick: () => navigate('/decouvrir'),
        }}
      />

      {/* SECTION 2 — Tableau comparatif (côte à côte) */}
      <SectionBackground page="pourquoi" section="pq-table" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le comparatif, ligne par ligne</span>
            <h2 className={h2Class}>
              Cabinet classique vs{' '}
              <span className={gradientText}>cabinet augmenté.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Huit axes structurels où nous nous différencions. Chaque ligne est
              contractuelle ou vérifiable — pas du discours marketing.
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-4">
            {COMPARE_ROWS.map((row, idx) => {
              const Icon = row.icon;
              return (
                <motion.div
                  key={row.axis}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="rounded-2xl bg-white ring-1 ring-neutral-200 overflow-hidden shadow-sm"
                >
                  {/* Axe header */}
                  <div className="flex items-center gap-3 px-5 md:px-6 py-3 bg-[#fbfaf8] border-b border-neutral-100">
                    <Icon className="w-4 h-4 text-[#d03484] flex-shrink-0" />
                    <h3 className="text-sm md:text-base font-bold text-[#30475e] uppercase tracking-wide">
                      {row.axis}
                    </h3>
                  </div>
                  {/* Comparaison côte à côte */}
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Cabinet classique */}
                    <div className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-neutral-100 bg-neutral-50/50">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-neutral-200">
                          <X className="w-3.5 h-3.5 text-neutral-500" />
                        </span>
                        <span className="text-xs font-bold text-neutral-500 uppercase tracking-widest">
                          Cabinet classique
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        {row.classic}
                      </p>
                    </div>
                    {/* recruteurs.io */}
                    <div className="p-5 md:p-6 bg-gradient-to-br from-[#fde7f2]/30 to-[#fde2d3]/30">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] shadow-sm">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </span>
                        <span className="text-xs font-bold uppercase tracking-widest">
                          <span className={gradientText}>recruteurs.io</span>
                        </span>
                      </div>
                      <p className="text-sm text-[#30475e] leading-relaxed">
                        <strong className={gradientText}>
                          {row.ioHighlight} —
                        </strong>{' '}
                        {row.io.replace(row.ioHighlight, '').replace(/^[,. ]+/, '')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Témoignages réutilisés */}
      <LinkedInRecommendations
        background="light"
        eyebrow="Ce que disent nos clients"
      />

      {/* SECTION 4 — FAQ */}
      <SectionBackground page="pourquoi" section="pq-faq" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Questions fréquentes</span>
              <h2 className={h2Class}>
                Les{' '}
                <span className={gradientText}>vraies objections</span>{' '}
                qu'on entend.
              </h2>
            </div>

            <div className="space-y-3">
              {FAQ_ITEMS.map((item, idx) => (
                <details
                  key={idx}
                  className="group rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/30 transition-all"
                >
                  <summary className="flex items-center justify-between gap-3 cursor-pointer px-5 py-4 text-sm md:text-base font-semibold text-[#30475e] hover:text-[#d03484] transition-colors">
                    <span>{item.q}</span>
                    <span className="flex-shrink-0 text-[#d03484] text-xl leading-none group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-neutral-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — CTA final */}
      <SectionBackground page="pourquoi" section="pq-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] p-10 md:p-14 text-white shadow-2xl text-center">
            <Sparkles className="w-10 h-10 text-[#fde7f2] mx-auto mb-5" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-5 leading-[1.1]">
              Comparer, c'est facile.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                Essayer, c'est gratuit.
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto">
              15 minutes de cadrage sans engagement. Vous repartez avec un
              diagnostic d'enjeu et un chiffrage transparent — même si vous
              décidez de ne pas travailler avec nous.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-2.5 text-sm font-bold text-[#30475e] hover:bg-[#fde7f2] hover:-translate-y-0.5 transition-all shadow-lg whitespace-nowrap"
              >
                Réserver mon échange
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/ressources/calculateur-roi')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-white/30 bg-white/10 backdrop-blur-sm px-6 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-all whitespace-nowrap"
              >
                Calculer mon ROI
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Avertissement marque employeur (preuve de transparence) */}
      <SectionBackground page="pourquoi" section="pq-disclaimer" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto rounded-2xl bg-[#fbfaf8] ring-1 ring-neutral-200 p-6 md:p-8 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-[#d03484] flex-shrink-0 mt-1" />
            <div className="text-sm text-neutral-600 leading-relaxed">
              <p className="font-semibold text-[#30475e] mb-2">
                Avertissement honnête.
              </p>
              <p>
                Tous les cabinets ne fonctionnent pas comme ce qu'on décrit dans
                la colonne "Cabinet classique". Certains font très bien leur
                travail. Cette page compare une <strong>pratique majoritaire
                du marché</strong> (telle qu'on l'a constatée chez nos clients
                qui nous ont quittés pour rejoindre) à <strong>notre
                propre pratique</strong>. C'est un comparatif de positionnement,
                pas une critique nominale.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
